import {
  pgEnum,
  varchar,
  boolean,
  uuid,
  index,
  pgTable,
  foreignKey,
  AnyPgColumn,
  jsonb,
} from 'drizzle-orm/pg-core';
import { organization } from '@trashlab/api/src/domains/identity/schemas/organization';
import { customer_contact } from '@trashlab/api/src/domains/customer/schemas/customer-contact';
import {
  defaultFields,
  defaultHistoryFields,
} from '@trashlab/api/drizzle/default';
import { phone_number } from '@trashlab/api/src/domains/comms-center/schemas/phone-number';
import { order_photos } from '@trashlab/api/src/domains/orders/schemas/order-photos';
import { IdentityUserSettings } from '@trashlab/api/src/domains/identity/types/identity-user-membership';
import { sql } from 'drizzle-orm';

/**
 * Enum defining the different roles a user can have in the system.
 * - OWNER: Full system access and organization ownership rights
 * - ADMIN: Administrative access to manage organization settings
 * - DRIVER: Access to driver-specific features and mobile app
 * - SALES_REP: Full access to sales and customer management features
 * - SALES_REP_LIMITED: Restricted access to sales features
 * - MAINTENANCE_ONLY: Access only to the maintenance module
 * - DISPATCHER_LIMITED: Dispatch/operations access without financial visibility
 */
export const business_role_type = pgEnum('business_role_type', [
  'OWNER',
  'ADMIN',
  'DRIVER',
  'SALES_REP',
  'SALES_REP_LIMITED',
  'MAINTENANCE_ONLY',
  'DISPATCHER_LIMITED',
  'ACCOUNTING',
]);

/**
 * Represents a user in the system with their identity and access information.
 * Users are associated with organizations and can have different roles.
 * Includes integration with authentication systems and communication preferences.
 */
export const identity_user = pgTable(
  'identity_user',
  {
    ...defaultFields.fieldsWithTenantId(),
    /** User's first name */
    firstName: varchar('firstName').notNull(),
    /** User's last name */
    lastName: varchar('lastName').notNull(),
    /** User's role in the organization */
    businessRole: business_role_type('businessRole').default('ADMIN').notNull(),
    /** Reference to the organization the user belongs to */
    organizationId: uuid('organizationId').notNull(),
    /** Reference to the user's Supabase auth account */
    supabaseUserId: uuid('supabaseUserId').notNull(),
    /** User's email address */
    emailAddress: varchar('emailAddress').notNull(),
    /** UUID of the user who last modified the record */
    lastModifiedBy: uuid('lastModifiedBy'),
    /** User's ID in the Tilled payment system */
    tilledUserId: varchar('tilledUserId'),
    /** UUID of the user who created the record */
    createdBy: uuid('createdBy'),
    /** User's phone number */
    phoneNumber: varchar('phoneNumber'),
    /** ID from legacy system if this record was migrated */
    legacySystemId: varchar('legacySystemId', { length: 255 }),
    /** Reference to associated customer contact record */
    contactId: uuid('contactId'),
    /** Whether the user is currently online in the comms system */
    commsOnline: boolean('commsOnline').default(false).notNull(),
    /** Whether the user has a valid voice SDK token */
    hasVoiceSDKToken: boolean('hasVoiceSDKToken').default(false).notNull(),
    /** Whether the user receives new dispatch notifications */
    receiveNewDispatchNotification: boolean('receiveNewDispatchNotification')
      .default(false)
      .notNull(),
    /** Reference to the last phone number used by the user */
    lastUsedPhoneNumberId: uuid('lastUsedPhoneNumberId'),
    /** Whether to prompt the user for their phone number */
    askForPhoneNumber: boolean('askForPhoneNumber').default(false).notNull(),
    /** Whether the user can access the web app on mobile devices */
    enableWebAppOnMobile: boolean('enableWebAppOnMobile')
      .default(false)
      .notNull(),
    /** Whether the user (usually drivers) can reorder schedule. Used mainly on mobile app */
    isScheduleReorderEnabled: boolean('isScheduleReorderEnabled')
      .default(true)
      .notNull(),
    /** Whether the user can be controlled by the AI agent */
    isAiEnhanced: boolean('isAiEnhanced').default(false).notNull(),
    /** Whether the user has the signature feature enabled in mobile */
    isSignatureEnabledOnMobile: boolean('isSignatureEnabledOnMobile')
      .default(false)
      .notNull(),
    /** Whether this is the special TrashLab support user for the tenant */
    isTrashLabSupportUser: boolean('isTrashLabSupportUser')
      .default(false)
      .notNull(),
    /** Whether the user can access Dashboard and Reporting sections */
    isDashboardReportingAccessEnabled: boolean(
      'isDashboardReportingAccessEnabled',
    )
      .default(true)
      .notNull(),
    /** Whether the user can access Settings sections */
    isSettingsAccessEnabled: boolean('isSettingsAccessEnabled')
      .default(true)
      .notNull(),
    /** Weighmaster license identifier for scale operations */
    weighmasterLicense: varchar('weighmasterLicense'),
    /** Job title shown on the worker's profile, e.g. "Dispatch Manager" */
    jobTitle: varchar('jobTitle', { length: 128 }),
    /** Free-text description of the worker's job and responsibilities */
    jobDescription: varchar('jobDescription', { length: 2000 }),
    /** User's profile photo */
    profilePhotoId: uuid('profilePhotoId').references(
      (): AnyPgColumn => order_photos.id,
    ),
    settings: jsonb('settings')
      .$type<IdentityUserSettings>()
      .default(sql`'{}'::jsonb`)
      .notNull(),
    /** Reference to the schedule (driver) this user is currently assigned to */
    scheduleId: uuid('scheduleId'),
  },
  (table) => {
    return {
      createdAtTs_idx: index('identity_user_createdAtTs_idx').on(
        table.createdAtTs,
      ),
      supabase_user_id_idx: index('identity_user_supabase_user_id_idx').on(
        table.supabaseUserId,
      ),
      tenantId_idx: index('identity_user_tenantId_idx').on(table.tenantId),
      tenantId_id_idx: index('identity_user_tenant_id_idx').on(
        table.tenantId,
        table.id,
      ),
      support_user_tenant_idx: index('identity_user_support_user_tenant_idx')
        .on(table.tenantId)
        .where(sql`${table.isTrashLabSupportUser} IS TRUE`),
      supportDirectoryIdx: index('identity_user_support_directory_idx')
        .using(
          'btree',
          table.organizationId,
          table.commsOnline.desc(),
          sql`lower(btrim(${table.firstName}::text || ' ' || ${table.lastName}::text))`,
          table.id,
        )
        .where(
          sql`${table.archivedAtTs} IS NULL AND ${table.isTrashLabSupportUser} IS FALSE`,
        ),
      emailAddress_trgm_idx: index('identity_user_email_address_trgm_idx')
        .using('gin', table.emailAddress.op('gin_trgm_ops'))
        .where(sql`${table.archivedAtTs} IS NULL`),
      fullName_trgm_idx: index('identity_user_full_name_trgm_idx')
        .using(
          'gin',
          sql`NULLIF(TRIM(COALESCE(${table.firstName}, '') || ' ' || COALESCE(${table.lastName}, '')), '') gin_trgm_ops`,
        )
        .where(sql`${table.archivedAtTs} IS NULL`),
      firstName_trgm_idx: index('identity_user_first_name_trgm_idx').using(
        'gin',
        sql`${table.firstName} public.gin_trgm_ops`,
      ),
      lastName_trgm_idx: index('identity_user_last_name_trgm_idx').using(
        'gin',
        sql`${table.lastName} public.gin_trgm_ops`,
      ),
      updatedAtTs_idx: index('identity_user_updatedAtTs_idx').on(
        table.updatedAtTs,
      ),
      updatedAtTs_idx1: index('identity_user_updatedAtTs_idx1').on(
        table.updatedAtTs.desc(),
      ),
      versionNumber_idx: index('identity_user_versionNumber_idx').on(
        table.versionNumber,
      ),
      contactId_idx: index('identity_user_contactId_idx').on(table.contactId),
      mobilePeopleIdx: index('identity_user_mobile_people_idx').on(table.tenantId, table.id).where(sql`${table.archivedAtTs} IS NULL`),
      mobilePeopleNameIdx: index('identity_user_mobile_people_name_idx').using('gin', sql`(${table.firstName} || ' ' || ${table.lastName}) gin_trgm_ops`).where(sql`${table.archivedAtTs} IS NULL`),
      organizationId_idx: index('identity_user_organizationId_idx').on(
        table.organizationId,
      ),
      changelogAudienceUserKeysetIdx: index(
        'changelog_audience_user_keyset_idx',
      )
        .on(
          table.organizationId,
          sql`LOWER(BTRIM(${table.firstName}::text || ' ' || ${table.lastName}::text)) COLLATE "C"`,
          table.id,
        )
        .where(
          sql`${table.archivedAtTs} IS NULL AND ${table.businessRole} <> 'DRIVER'::public.business_role_type AND NOT ${table.isTrashLabSupportUser}`,
        ),
      lastUsedPhoneNumberId_idx: index(
        'identity_user_lastUsedPhoneNumberId_idx',
      ).on(table.lastUsedPhoneNumberId),

      scheduleId_idx: index('identity_user_scheduleId_idx').on(
        table.scheduleId,
      ),

      identity_user_contactId_fkey: foreignKey({
        columns: [table.contactId],
        foreignColumns: [customer_contact.id],
        name: 'identity_user_contactId_fkey',
      }),
      identity_user_organizationId_fkey: foreignKey({
        columns: [table.organizationId],
        foreignColumns: [organization.id],
        name: 'identity_user_organizationId_fkey',
      }),
    };
  },
);

/**
 * Historical record of changes to user identities.
 * Tracks all modifications to user records over time for auditing and versioning.
 * Contains the same fields as identity_user but allows nulls for partial updates.
 */
export const identity_user_history = pgTable('identity_user_history', {
  ...defaultHistoryFields.historyFieldsWithTenantId(),
  firstName: varchar('firstName'),
  lastName: varchar('lastName'),
  businessRole: business_role_type('businessRole'),
  organizationId: uuid('organizationId'),
  supabaseUserId: uuid('supabaseUserId'),
  emailAddress: varchar('emailAddress'),
  lastModifiedBy: uuid('lastModifiedBy'),
  tilledUserId: varchar('tilledUserId'),
  createdBy: uuid('createdBy'),
  phoneNumber: varchar('phoneNumber'),
  legacySystemId: varchar('legacySystemId', { length: 255 }),
  contactId: uuid('contactId'),
  commsOnline: boolean('commsOnline'),
  hasVoiceSDKToken: boolean('hasVoiceSDKToken'),
  lastUsedPhoneNumberId: uuid('lastUsedPhoneNumberId'),
  receiveNewDispatchNotification: boolean('receiveNewDispatchNotification'),
  askForPhoneNumber: boolean('askForPhoneNumber'),
  enableWebAppOnMobile: boolean('enableWebAppOnMobile'),
  isScheduleReorderEnabled: boolean('isScheduleReorderEnabled'),
  isAiEnhanced: boolean('isAiEnhanced'),
  isSignatureEnabledOnMobile: boolean('isSignatureEnabledOnMobile'),
  isTrashLabSupportUser: boolean('isTrashLabSupportUser'),
  isDashboardReportingAccessEnabled: boolean(
    'isDashboardReportingAccessEnabled',
  ),
  isSettingsAccessEnabled: boolean('isSettingsAccessEnabled'),
  weighmasterLicense: varchar('weighmasterLicense'),
  jobTitle: varchar('jobTitle', { length: 128 }),
  jobDescription: varchar('jobDescription', { length: 2000 }),
  profilePhotoId: uuid('profilePhotoId'),
  settings: jsonb('settings'),
  scheduleId: uuid('scheduleId'),
});

/** Type inference for identity user schema */
export type SelectUser = typeof identity_user.$inferSelect;
